/* eslint-disable no-console */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

const loaderStyle = `
  @keyframes bta-spin { to { transform: rotate(360deg); } }
  .bta-suspense-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 0;
  }
  .bta-suspense-loader::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #637381;
    border-radius: 50%;
    animation: bta-spin 0.7s linear infinite;
  }
`;

const LOADER_DELAY_MS = 200;

function ErrorFallback() {
  const errorFallbackStyle = {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#637381',
    fontSize: '14px',
    lineHeight: '1.5',
  };

  return (
    <div style={errorFallbackStyle}>
      <p>Something went wrong during BookThatApp loading.</p>
      <p>Please refresh the page or contact the store for assistance.</p>
    </div>
  );
}

function SuspenseFallback() {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setVisible(true), LOADER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <>
      <style>{loaderStyle}</style>
      <div className="bta-suspense-loader" />
    </>
  );
}

const COMPONENT_ATTRIBUTE = 'data-react-component';
const COMPONENT_PROPS_ATTRIBUTE = 'data-react-props';

const NO_SPINNER_COMPONENTS = new Set(['Layout', 'LayoutContainer']);

// Tracks React 18 roots by DOM element so we can unmount cleanly.
const roots = new WeakMap();

function unmountComponents() {
  const containers = document.querySelectorAll(`[${COMPONENT_ATTRIBUTE}]`);

  containers.forEach((containerElement) => {
    const root = roots.get(containerElement);
    if (root) {
      root.unmount();
      roots.delete(containerElement);
    }
  });
}

function readJsonData(element, attribute) {
  const text = attribute
    ? element?.getAttribute(attribute)
    : element?.textContent;
  const data =
    typeof text === 'string' ? JSON.parse(text.trim() || 'null') : null;
  if (data != null && typeof data === 'object') {
    return data;
  }
  return {};
}

function doMount(registeredComponents) {
  // Retrieve component mount points and their props following this pattern:
  // <div data-react-component="name" data-react-props="<%= some_props.to_json %>"></div>
  const componentMountPoints = document.querySelectorAll(
    `[${COMPONENT_ATTRIBUTE}]`
  );

  componentMountPoints.forEach((containerElement) => {
    const name = containerElement.getAttribute(COMPONENT_ATTRIBUTE);
    const props = readJsonData(containerElement, COMPONENT_PROPS_ATTRIBUTE);

    if (
      name &&
      Object.prototype.hasOwnProperty.call(registeredComponents, name)
    ) {
      const Component = registeredComponents[name];

      let root = roots.get(containerElement);
      if (!root) {
        root = createRoot(containerElement);
        roots.set(containerElement, root);
      }

      root.render(
        <React.StrictMode>
          <ErrorBoundary
            fallback={<ErrorFallback />}
            onError={(error, info) => console.error('BTA: Component rendering error', { error, info, component: name })}
          >
            <React.Suspense fallback={NO_SPINNER_COMPONENTS.has(name) ? null : <SuspenseFallback />}>
              <Component {...props} />
            </React.Suspense>
          </ErrorBoundary>
        </React.StrictMode>
      );
    } else if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Cannot mount unknown component '${name}'.\nUse one of: ${Object.keys(
          registeredComponents
        ).join(', ')}.`
      );
    }
  });
}

export default function register(components) {
  const onReady = () => {
    doMount(components);
  };

  document.addEventListener('turbo:before-render', unmountComponents);
  document.addEventListener('turbo:load', onReady);
  document.addEventListener('turbo:frame-load', onReady);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
}
